import Typography from '@ui/typography'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { itemFontSize } from './styles'
import cn from '@ui/utils/cn'
import { getClosestValue, isOptionEnabled } from './PickerUtils'

type Value = string
type Options = { label: string; value: Value }[]
type OnChange = (value: Value) => void
type Timeout = NodeJS.Timeout | null

interface Props {
  value1: Value
  value2: Value
  min?: number
  max?: number
  onChange1: OnChange
  onChange2: OnChange
  options1: Options
  options2: Options
  isDoubleDigit?: boolean
  loopOptions1?: boolean
  loopSegments1?: number
}

const optionHeight = 64
const displayItems = 5 // must be odd
const containerHeight = displayItems * optionHeight
const spaceHeight = ((displayItems - 1) / 2) * optionHeight

export default function PickerUI(props: Props) {
  const {
    value1,
    value2,
    onChange1,
    onChange2,
    options1,
    options2,
    isDoubleDigit,
    loopOptions1,
    loopSegments1,
    min,
    max,
  } = props

  const loopSegments = useMemo(() => {
    if (!loopOptions1) return 1
    const segments = loopSegments1 ?? 5
    if (segments <= 1) return 1
    return segments % 2 === 0 ? segments + 1 : segments
  }, [loopOptions1, loopSegments1])

  const loopSeparator = '__loop__'

  const makeLoopValue = useCallback(
    (base: string, segment: number) =>
      loopOptions1 ? `${base}${loopSeparator}${segment}` : base,
    [loopOptions1, loopSeparator]
  )

  const parseLoopValue = useCallback(
    (value: string) => {
      if (!loopOptions1 || !value.includes(loopSeparator)) {
        return { base: value, segment: Math.floor(loopSegments / 2) }
      }
      const [base, segment] = value.split(loopSeparator)
      const parsedSegment = Number(segment)
      return {
        base,
        segment: Number.isFinite(parsedSegment)
          ? parsedSegment
          : Math.floor(loopSegments / 2),
      }
    },
    [loopOptions1, loopSegments, loopSeparator]
  )

  const loopedOptions1 = useMemo(() => {
    if (!loopOptions1 || loopSegments <= 1 || options1.length === 0) {
      return options1
    }

    const repeated = Array.from({ length: loopSegments }, (_, repeatIndex) =>
      options1.map((option) => ({
        label: option.label,
        value: makeLoopValue(option.value, repeatIndex),
      }))
    ).flat()

    return repeated
  }, [loopOptions1, loopSegments, options1, makeLoopValue])

  const middleSegment = useMemo(
    () => Math.floor(loopSegments / 2),
    [loopSegments]
  )

  const getLoopValue = useCallback(
    (base: string) => {
      if (!loopOptions1 || loopSegments <= 1) {
        return base
      }
      const exists = options1.some((option) => option.value === base)
      const normalizedBase =
        exists || options1.length === 0 ? base : options1[0].value
      return makeLoopValue(normalizedBase, middleSegment)
    },
    [loopOptions1, loopSegments, options1, makeLoopValue, middleSegment]
  )

  const [selectedLoopValue1, setSelectedLoopValue1] = useState<string>(() =>
    getLoopValue(value1)
  )

  useEffect(() => {
    if (!loopOptions1 || loopSegments <= 1) {
      return
    }
    setSelectedLoopValue1((current) => {
      if (!current) return getLoopValue(value1)
      const { base } = parseLoopValue(current)
      if (base !== value1) {
        return getLoopValue(value1)
      }
      return current
    })
  }, [value1, loopOptions1, loopSegments, getLoopValue, parseLoopValue])

  const isDisabled = useCallback(
    (v: string) => {
      return !isOptionEnabled({ min, max, value1, value2: v })
    },
    [min, max, value1]
  )

  return (
    <div
      className="relative inset-0 my-8 flex w-full flex-row items-center"
      style={{ height: containerHeight }}
    >
      <ActiveZone />
      <PickerList
        value={loopOptions1 && loopSegments > 1 ? selectedLoopValue1 : value1}
        options={loopOptions1 && loopSegments > 1 ? loopedOptions1 : options1}
        onChange={(candidate) => {
          if (loopOptions1 && loopSegments > 1) {
            setSelectedLoopValue1(candidate)
            const { base } = parseLoopValue(candidate)
            onChange1(base)
          } else {
            onChange1(candidate)
          }
        }}
        className={cn(
          isDoubleDigit ? 'w-1/2 items-end' : 'w-full items-center'
        )}
      />
      <Typography
        variant="XLB"
        className={cn(
          'flex select-none items-center',
          isDoubleDigit ? 'opacity-100' : 'opacity-0'
        )}
      >
        .
      </Typography>
      <PickerList
        value={value2}
        options={options2}
        onChange={onChange2}
        className={cn(
          'items-start',
          isDoubleDigit
            ? 'w-1/2 opacity-100'
            : 'pointer-events-none w-0 opacity-0'
        )}
        isDisabled={isDisabled}
      />
      <Gradient className="top-0 bg-gradient-to-b" />
      <Gradient className="bottom-0 bg-gradient-to-t" />
    </div>
  )
}

interface ListProps {
  value: Value
  options: Options
  className: string
  onChange: OnChange
  isDisabled?: (value: string) => boolean
}

const PickerList = forwardRef<HTMLDivElement, ListProps>(
  ({ value, options, onChange, className, isDisabled }, ref) => {
    const listRef = useRef<HTMLDivElement>(null)
    const scrollTimeout = useRef<Timeout>(null)

    const init = useRef(false)

    const scrollToValue = useCallback(
      (value: Value, behavior: 'smooth' | 'instant' = 'smooth') => {
        if (!listRef.current) return
        const index = options.findIndex((option) => option.value === value)
        if (index < 0) return
        const offset = index * optionHeight
        listRef.current.scrollTo({ top: offset, behavior })
      },
      [options]
    )

    const handleScrollEnd = useCallback(() => {
      if (!listRef.current) return
      const offset = listRef.current?.scrollTop || 0
      const index = Math.round(offset / optionHeight)
      const selectedValue = options[index]?.value
      if (!selectedValue) return
      const disabled = isDisabled?.(selectedValue) ?? false
      if (disabled) {
        const closestValue = getClosestValue({
          options,
          isDisabled,
          index,
        })
        if (!closestValue) return
        scrollToValue(closestValue)
        return
      }
    }, [options, isDisabled, scrollToValue])

    const handleScroll = useCallback(() => {
      if (!listRef.current) return
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current)
      }
      scrollTimeout.current = setTimeout(() => {
        const offset = listRef.current?.scrollTop || 0
        const index = Math.round(offset / optionHeight)
        const selectedValue = options[index]?.value
        if (!selectedValue) return

        const disabled = isDisabled?.(selectedValue) ?? false

        if (disabled) {
          return
        }

        onChange(selectedValue)
      }, 100)
    }, [options, isDisabled, value, onChange])

    const handleWheel = useCallback((event: WheelEvent) => {
      event.preventDefault()
      listRef.current?.scrollBy({
        top: event.deltaY > 0 ? optionHeight : -optionHeight,
        behavior: 'instant',
      })
    }, [])

    useEffect(() => {
      scrollToValue(value, !init.current ? 'instant' : 'smooth')
      init.current = true
    }, [value, options, scrollToValue])

    useEffect(() => {
      if (isDisabled) {
        handleScrollEnd()
      }
    }, [isDisabled, handleScrollEnd])

    useEffect(() => {
      const currentRef = listRef.current
      if (!currentRef) return

      currentRef.addEventListener('scrollend', handleScrollEnd)
      currentRef.addEventListener('scroll', handleScroll)
      currentRef.addEventListener('wheel', handleWheel)

      return () => {
        currentRef.removeEventListener('scrollend', handleScrollEnd)
        currentRef.removeEventListener('scroll', handleScroll)
        currentRef.removeEventListener('wheel', handleWheel)
      }
    }, [handleScrollEnd, handleScroll, handleWheel])

    const styleOption = {
      height: optionHeight,
      maxHeight: optionHeight,
      minHeight: optionHeight,
      scrollSnapAlign: 'center',
    }

    const styleDummy = {
      width: '100%',
      height: spaceHeight,
      maxHeight: spaceHeight,
      minHeight: spaceHeight,
      scrollSnapAlign: 'center',
    }

    return (
      <div
        ref={listRef}
        className={cn('flex h-full flex-col overflow-y-scroll', className)}
        style={{ scrollSnapType: 'y mandatory' }}
      >
        <div style={styleDummy} />
        {options.map((option) => {
          const disabled = isDisabled?.(option.value)
          return (
            <div
              key={option.value}
              className="flex w-24 cursor-pointer items-center justify-center"
              style={styleOption}
              onClick={() => {
                if (!disabled) {
                  onChange(option.value)
                }
              }}
            >
              <Typography
                variant="XL"
                className={cn(
                  'select-none text-center',
                  disabled && 'color-red-400'
                )}
                style={{ fontSize: itemFontSize }}
              >
                {option.label}
              </Typography>
            </div>
          )
        })}
        <div style={styleDummy} />
      </div>
    )
  }
)

PickerList.displayName = 'PickerUIList'

const ActiveZone = () => (
  <div
    className="bg-gray-75 pointer-events-none absolute left-0 w-full rounded-lg"
    style={{
      height: optionHeight,
      top: spaceHeight,
      left: 0,
    }}
  />
)

const Gradient = ({ className }: { className: string }) => (
  <div
    className={cn(
      'pointer-events-none absolute left-0 w-full from-white via-white/50 to-white/0',
      className
    )}
    style={{
      height: spaceHeight,
    }}
  />
)
